/**
 * Task Management Store
 * Zustand store for managing task-related UI state
 * Per-page filters are handled by usePageFilters from pageFiltersStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TaskState {
  // View preferences (persisted)
  calendarViewMode: 'month' | 'week' | 'day' | 'agenda';
  listViewMode: 'list' | 'grid';
  sidebarOpen: boolean;

  // Admin projects page (persisted)
  selectedProjectId: number | null;
  showClosedProjects: boolean;

  // Selection state (not persisted)
  selectedTaskIds: Set<number>;
  isBulkModeActive: boolean;

  // Navigation context (not persisted)
  activeTaskId: number | null;
  returnPath: string | null;

  // Quick task modal (not persisted)
  quickTaskModalOpen: boolean;

  // Layout override (not persisted): admin grouping pages flip this to true
  // while a Kanban board is on-screen so MainLayout escapes the boxed container
  // and lets columns span the full viewport. Reset on unmount.
  adminKanbanFullWidth: boolean;
}

interface TaskActions {
  // View preferences
  setCalendarViewMode: (mode: 'month' | 'week' | 'day' | 'agenda') => void;
  setListViewMode: (mode: 'list' | 'grid') => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Admin projects
  setSelectedProjectId: (id: number | null) => void;
  setShowClosedProjects: (show: boolean) => void;

  // Selection
  toggleTaskSelection: (id: number) => void;
  selectAllTasks: (ids: number[]) => void;
  clearSelection: () => void;
  setBulkMode: (active: boolean) => void;

  // Navigation
  setActiveTaskId: (id: number | null) => void;
  setReturnPath: (path: string | null) => void;

  // Quick task modal
  setQuickTaskModalOpen: (open: boolean) => void;

  // Layout override
  setAdminKanbanFullWidth: (full: boolean) => void;
}

type TaskStore = TaskState & TaskActions;

export const useTaskStore = create<TaskStore>()(
  persist(
    (set, get) => ({
      // Initial state
      calendarViewMode: 'month',
      listViewMode: 'list',
      sidebarOpen: true,
      selectedProjectId: null,
      showClosedProjects: false,
      selectedTaskIds: new Set(),
      isBulkModeActive: false,
      activeTaskId: null,
      returnPath: null,
      quickTaskModalOpen: false,
      adminKanbanFullWidth: false,

      // View preferences
      setCalendarViewMode: (mode) => set({ calendarViewMode: mode }),
      setListViewMode: (mode) => set({ listViewMode: mode }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      // Admin projects
      setSelectedProjectId: (id) => set({ selectedProjectId: id }),
      setShowClosedProjects: (show) => set({ showClosedProjects: show }),

      // Selection
      toggleTaskSelection: (id) => {
        set((state) => {
          const newSet = new Set(state.selectedTaskIds);
          if (newSet.has(id)) {
            newSet.delete(id);
          } else {
            newSet.add(id);
          }
          return { selectedTaskIds: newSet };
        });
      },
      selectAllTasks: (ids) => set({ selectedTaskIds: new Set(ids) }),
      clearSelection: () => set({ selectedTaskIds: new Set(), isBulkModeActive: false }),
      setBulkMode: (active) => {
        set({ isBulkModeActive: active });
        if (!active) get().clearSelection();
      },

      // Navigation
      setActiveTaskId: (id) => set({ activeTaskId: id }),
      setReturnPath: (path) => set({ returnPath: path }),

      // Quick task modal
      setQuickTaskModalOpen: (open) => set({ quickTaskModalOpen: open }),

      // Layout override
      setAdminKanbanFullWidth: (full) => set({ adminKanbanFullWidth: full }),
    }),
    {
      name: 'task-store',
      partialize: (state) => ({
        calendarViewMode: state.calendarViewMode,
        listViewMode: state.listViewMode,
        sidebarOpen: state.sidebarOpen,
        selectedProjectId: state.selectedProjectId,
        showClosedProjects: state.showClosedProjects,
      }),
    }
  )
);

// Selectors
export const selectCalendarViewMode = (state: TaskStore) => state.calendarViewMode;
export const selectListViewMode = (state: TaskStore) => state.listViewMode;
export const selectSelectedTaskIds = (state: TaskStore) => state.selectedTaskIds;
export const selectIsBulkModeActive = (state: TaskStore) => state.isBulkModeActive;
export const selectReturnPath = (state: TaskStore) => state.returnPath;
export const selectQuickTaskModalOpen = (state: TaskStore) => state.quickTaskModalOpen;
